import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Leaf, ChevronDown, Globe } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentLanguage, changeLanguage, t, languages } = useLanguage();
  
  // Timeout refs for delayed closing
  const servicesTimeoutRef = useRef(null);
  const languageTimeoutRef = useRef(null);

  // Services links with translations
  const servicesLinks = [
    { name: t('service-co2-web'), to: `/${currentLanguage}/services/co2-neutral-web` },
    { name: t('service-calculator'), to: `/${currentLanguage}/services/co2-calculator` },
    { name: t('service-certificate'), to: `/${currentLanguage}/services/co2-certificate` },
    { name: t('service-exchange'), to: `/${currentLanguage}/services/co2-exchange` },
  ];

  // Pages that should always have white navbar
  const whiteNavbarPages = [`/${currentLanguage}/certificate-verification`, `/${currentLanguage}/add-co2-badge`];
  const shouldUseWhiteNavbar = whiteNavbarPages.includes(location.pathname);

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
    setLanguageOpen(false);
    // Clear timeouts when location changes
    if (servicesTimeoutRef.current) {
      clearTimeout(servicesTimeoutRef.current);
      servicesTimeoutRef.current = null;
    }
    if (languageTimeoutRef.current) {
      clearTimeout(languageTimeoutRef.current);
      languageTimeoutRef.current = null;
    }
  }, [location]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (servicesTimeoutRef.current) {
        clearTimeout(servicesTimeoutRef.current);
      }
      if (languageTimeoutRef.current) {
        clearTimeout(languageTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    setIsOpen(false);
    const isHomePage = [`/${currentLanguage}`, `/tr`, `/de`, `/en`, `/`].includes(location.pathname);

    if (!isHomePage) {
      // Navigate to home page first, then scroll to the section
      navigate(`/${currentLanguage}`);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 600);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setLanguageOpen(false);
    
    // Update URL to reflect new language
    const currentPath = location.pathname;
    const newPath = currentPath.replace(/^\/(tr|de|en)/, `/${langCode}`);
    navigate(newPath);
  };

  // Determine navbar styling based on page and scroll state
  const getNavbarStyle = () => {
    if (shouldUseWhiteNavbar) {
      return 'bg-white shadow-lg';
    }
    return scrolled ? 'bg-white shadow-lg' : 'bg-transparent';
  };

  const getTextColor = (isLogo = false) => {
    if (shouldUseWhiteNavbar) {
      return isLogo ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-500';
    }
    return scrolled ? (isLogo ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-500') : 'text-white hover:text-primary-200';
  };

  const getLogoColor = () => {
    if (shouldUseWhiteNavbar) {
      return 'text-primary-500';
    }
    return scrolled ? 'text-primary-500' : 'text-white';
  };

  const getCurrentLanguageInfo = () => {
    return languages.find(lang => lang.code === currentLanguage) || languages[0];
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${getNavbarStyle()}`}>
      <div className="container-custom px-4">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to={`/${currentLanguage}`} className="flex items-center space-x-2 flex-shrink-0">
            <Leaf className={`h-6 w-6 lg:h-8 lg:w-8 ${getLogoColor()}`} />
            <span className={`text-lg lg:text-2xl font-bold ${getTextColor(true)} hidden sm:block`}>
              CO2 Neutral
            </span>
            <span className={`text-lg font-bold ${getTextColor(true)} sm:hidden`}>
              CO2
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
            <button
              onClick={() => scrollToSection('about')}
              className={`font-medium transition-colors duration-200 ${getTextColor()}`}
            >
              {t('about')}
            </button>
            <div className="relative group">
              <button
                onClick={() => setServicesOpen((v) => !v)}
                className={`font-medium flex items-center gap-1 transition-colors duration-200 ${getTextColor()}`}
                onMouseEnter={() => {
                  if (servicesTimeoutRef.current) {
                    clearTimeout(servicesTimeoutRef.current);
                    servicesTimeoutRef.current = null;
                  }
                  setServicesOpen(true);
                }}
                onMouseLeave={() => {
                  servicesTimeoutRef.current = setTimeout(() => {
                    setServicesOpen(false);
                  }, 300); // 300ms delay before closing
                }}
              >
                {t('services')} <ChevronDown className="h-4 w-4" />
              </button>
              {servicesOpen && (
                <div 
                  onMouseEnter={() => {
                    if (servicesTimeoutRef.current) {
                      clearTimeout(servicesTimeoutRef.current);
                      servicesTimeoutRef.current = null;
                    }
                    setServicesOpen(true);
                  }} 
                  onMouseLeave={() => {
                    servicesTimeoutRef.current = setTimeout(() => {
                      setServicesOpen(false);
                    }, 300); // 300ms delay before closing
                  }} 
                  className="absolute left-0 mt-2 bg-white shadow-lg rounded-lg py-2 min-w-[220px] z-50"
                >
                  {servicesLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="block px-5 py-2 text-neutral-700 hover:bg-primary-50 hover:text-primary-600 font-medium transition-colors"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link
              to={`/${currentLanguage}/certificate-verification`}
              className={`font-medium transition-colors duration-200 ${getTextColor()}`}
            >
              {t('certificate-verify')}
            </Link>
            <Link
              to={`/${currentLanguage}/add-co2-badge`}
              className={`font-medium transition-colors duration-200 ${getTextColor()}`}
            >
              {t('add-badge')}
            </Link>
            <button
              onClick={() => scrollToSection('certificate-form')}
              className={`btn-primary ${shouldUseWhiteNavbar ? '' : (scrolled ? '' : 'bg-white/20 border border-white text-white hover:bg-white/40')}`}
            >
              {t('get-certificate')}
            </button>

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setLanguageOpen((v) => !v)}
                className={`flex items-center gap-2 font-medium transition-colors duration-200 ${getTextColor()}`}
                onMouseEnter={() => {
                  if (languageTimeoutRef.current) {
                    clearTimeout(languageTimeoutRef.current);
                    languageTimeoutRef.current = null;
                  }
                  setLanguageOpen(true);
                }}
                onMouseLeave={() => {
                  languageTimeoutRef.current = setTimeout(() => {
                    setLanguageOpen(false);
                  }, 300); // 300ms delay before closing
                }}
              >
                <Globe className="h-4 w-4" />
                <span className="text-lg">{getCurrentLanguageInfo().flag}</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              {languageOpen && (
                <div 
                  onMouseEnter={() => {
                    if (languageTimeoutRef.current) {
                      clearTimeout(languageTimeoutRef.current);
                      languageTimeoutRef.current = null;
                    }
                    setLanguageOpen(true);
                  }} 
                  onMouseLeave={() => {
                    languageTimeoutRef.current = setTimeout(() => {
                      setLanguageOpen(false);
                    }, 300); // 300ms delay before closing
                  }} 
                  className="absolute right-0 mt-2 bg-white shadow-lg rounded-lg py-2 min-w-[160px] z-50"
                >
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full text-left px-4 py-2 flex items-center gap-3 transition-colors ${
                        currentLanguage === lang.code 
                          ? 'bg-primary-50 text-primary-600 font-medium' 
                          : 'text-neutral-700 hover:bg-neutral-50'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile Language Selector */}
            <div className="relative">
              <button
                onClick={() => setLanguageOpen((v) => !v)}
                className={`flex items-center gap-1 transition-colors duration-200 ${getTextColor()}`}
              >
                <Globe className="h-4 w-4" />
                <span className="text-base">{getCurrentLanguageInfo().flag}</span>
              </button>
              {languageOpen && (
                <div className="absolute right-0 mt-2 bg-white shadow-lg rounded-lg py-2 min-w-[140px] z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors text-sm ${
                        currentLanguage === lang.code 
                          ? 'bg-primary-50 text-primary-600 font-medium' 
                          : 'text-neutral-700 hover:bg-neutral-50'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setIsOpen((v) => !v)}
              className="inline-flex items-center justify-center p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              aria-label="Menüyü Aç/Kapat"
            >
              {isOpen ? (
                <X className={`h-6 w-6 ${getTextColor()}`} />
              ) : (
                <Menu className={`h-6 w-6 ${getTextColor()}`} />
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black bg-opacity-40" onClick={() => setIsOpen(false)}>
          <div className="absolute top-0 right-0 w-4/5 max-w-xs h-full bg-white shadow-lg p-4 flex flex-col gap-3 overflow-y-auto" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setIsOpen(false)}
              className="self-end mb-2"
              aria-label="Menüyü Kapat"
            >
              <X className="h-6 w-6 text-neutral-700" />
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="font-medium text-neutral-700 py-2 text-left w-full text-sm"
            >
              {t('about')}
            </button>
            <div className="relative">
              <button
                onClick={() => setServicesOpen((v) => !v)}
                className="font-medium flex items-center gap-1 text-neutral-700 py-2 w-full text-left text-sm"
              >
                {t('services')} <ChevronDown className="h-4 w-4" />
              </button>
              {servicesOpen && (
                <div className="ml-4 mt-1 bg-neutral-50 rounded-lg shadow-inner">
                  {servicesLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-2 text-neutral-700 hover:bg-primary-50 hover:text-primary-600 font-medium transition-colors text-sm"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link
              to={`/${currentLanguage}/certificate-verification`}
              onClick={() => setIsOpen(false)}
              className="font-medium text-neutral-700 py-2 text-left w-full text-sm"
            >
              {t('certificate-verify')}
            </Link>
            <Link
              to={`/${currentLanguage}/add-co2-badge`}
              onClick={() => setIsOpen(false)}
              className="font-medium text-neutral-700 py-2 text-left w-full text-sm"
            >
              {t('add-badge')}
            </Link>
            <button
              onClick={() => scrollToSection('certificate-form')}
              className="btn-primary w-full mt-2 text-sm py-2"
            >
              {t('get-certificate')}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar; 